import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { FloatingPortal } from '@floating-ui/react';
import { useClickAway } from '@uidotdev/usehooks';
import { Upload } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { useHotkeys } from 'react-hotkeys-hook';

import { apiPatch, apiPatchAsUser } from '../lib/api_v2';
import db from '../lib/db';
import { ensureEmojiCache } from '../lib/emojiCache';
import { persistFlexiconSaveResponse } from '../lib/flexiconSaveResponse';
import { logDebug, logError } from '../lib/logger';
import {
  MediaAttachmentStates,
  enqueueUpload,
  isUploadActiveState,
  removeMediaAttachmentsForSessionKey,
  useMediaAttachment,
} from '../lib/media_attachments_store';
import { getMetaContent } from '../lib/meta';
import { uploadUserFlexicon } from '../lib/user_flexicon_upload';
import { cn } from '../lib/utils';

import { KEYCHAIN, useRegisterWithKeyMaster } from '../contexts/KeyMaster';
import useScopedClipboardFiles from '../hooks/useScopedClipboardFiles';
import EmojibarPopover from './EmojibarPopover';
import MediaAttachmentUploadPreview from './MediaAttachmentUploadPreview';
import Flexicon, { getFlexiconSignature, resolveImageFlexiconUrl } from './shared/Flexicon';

const FLEXICON_IMAGE_CONTENT_TYPES = JSON.parse(getMetaContent('channel:flexicon_image_content_types') || '[]');

const FLEXICON_RESOURCE_TYPES = new Set(['user', 'channel', 'workspace']);

const UPLOAD_PURPOSE_BY_RESOURCE_TYPE = {
  user: 'user_flexicon',
  channel: 'channel_flexicon',
  workspace: 'workspace_flexicon',
};

const BUTTON_LABELS_BY_RESOURCE_TYPE = {
  user: {
    upload: 'Upload avatar',
    remove: 'Remove avatar',
  },
  channel: {
    upload: 'Upload icon',
    remove: 'Remove icon',
  },
  workspace: {
    upload: 'Upload logo',
    remove: 'Remove logo',
  },
};

const SAVE_ERROR_MESSAGES_BY_RESOURCE_TYPE = {
  user: 'Unable to save avatar. Please try again.',
  channel: 'Unable to save icon. Please try again.',
  workspace: 'Unable to save logo. Please try again.',
};

const SAVE_PATH_BY_RESOURCE_TYPE = {
  workspace: (resourceId) => (resourceId ? `/workspaces/${resourceId}` : null),
  user: () => '/me',
  channel: (resourceId) => (resourceId ? `/channels/${resourceId}` : null),
};

function resolveFlexiconSavePath(resourceType, resourceId) {
  return SAVE_PATH_BY_RESOURCE_TYPE[resourceType]?.(resourceId) ?? null;
}

function findLetterGlyph(letterGlyphs, nameValue = '') {
  const letter = nameValue.toLowerCase().match(/[a-z]/)?.[0] || '_';
  return letterGlyphs.find((glyph) => glyph.filename === letter) || letterGlyphs.find((glyph) => glyph.filename === '_') || null;
}

const ActionButton = ({ onClick, disabled, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="cursor-pointer rounded-xl border border-gray-300 bg-white px-4 py-3.5 text-cursed text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
  >
    {children}
  </button>
);

const FlexiconEditor = forwardRef(function FlexiconEditor(
  {
    resource,
    resourceType,
    keychainId = null,
    nameValue = '',
    readOnly = false,
    layout = 'inline',
    // 'buttons' renders the Upload/Emoji/Remove row; 'overlay' replaces it with a dark
    // upload badge over the preview's bottom-right corner (upload-only affordance).
    actionsPresentation = 'buttons',
    previewSize = 'size-32',
    previewRounded = 'rounded-full',
    uploadOnly = false,
    uploadLabel = null,
    uploadBusyLabel = 'Uploading image',
    uploadButtonClassName = '',
    showUploadButton = true,
    onSaveComplete,
    onFlexiconChange,
    onUploadingChange,
  },
  ref,
) {
  if (resource == null || !FLEXICON_RESOURCE_TYPES.has(resourceType)) {
    throw new Error(
      `FlexiconEditor: resource and resourceType are required; resourceType must be one of ${[...FLEXICON_RESOURCE_TYPES].join(', ')}.`,
    );
  }

  const resourceId = resource?.id;

  // workspace/user always exist; channel may be unsaved
  const isUnsaved = resourceType !== 'workspace' && resourceType !== 'user' && !resourceId;

  // Dynamic session key based on resource type for workspace/channel queue cleanup.
  const uploadSessionKey = useMemo(() => `flexicon-editor2:${resourceType}:${resourceId || 'new'}`, [resourceType, resourceId]);
  const uploadWorkspaceId = useMemo(
    () => (resourceType === 'workspace' && resourceId ? resourceId : undefined),
    [resourceType, resourceId],
  );

  const [isEmojibarOpen, setIsEmojibarOpen] = useState(false);
  const [currentUploadId, setCurrentUploadId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [savedAttachmentId, setSavedAttachmentId] = useState(null);
  const [previewFlexicon, setPreviewFlexicon] = useState(null);
  const [userUploadPreviewUrl, setUserUploadPreviewUrl] = useState(null);
  const [userUploadProgress, setUserUploadProgress] = useState(0);
  const [isUserUploadProcessing, setIsUserUploadProcessing] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);
  const rootRef = useRef(null);

  // Use the shared media attachment store snapshot so upload polling/completion
  // updates stay in sync with the current ownership/session model.
  const currentUpload = useMediaAttachment(currentUploadId);

  const isUploading = isUploadActiveState(currentUpload?.upload?.state);
  const isProcessing = currentUpload?.upload?.state === MediaAttachmentStates.processing;
  const isUploadComplete = currentUpload?.upload?.state === MediaAttachmentStates.completed;
  const isUserUploading = Boolean(userUploadPreviewUrl);

  // Show upload preview until we clear currentUploadId (after save completes)
  const showUploadPreview = currentUpload != null;
  // readOnly: workspace settings pass !isAdmin so clipboard/drag cannot start uploads (fieldset does not catch those)
  const isDisabled = readOnly || isSaving || isUserUploading || (showUploadPreview && (isUploading || isProcessing));

  // Notify parent when upload state changes
  useEffect(() => {
    onUploadingChange?.(isUploading || isProcessing || isUserUploading || isUserUploadProcessing);
  }, [isUploading, isProcessing, isUserUploading, isUserUploadProcessing, onUploadingChange]);

  useEffect(() => {
    if (!userUploadPreviewUrl) return undefined;
    return () => URL.revokeObjectURL(userUploadPreviewUrl);
  }, [userUploadPreviewUrl]);

  // letter glyphs — seeded into sessionStorage by live_journal
  const [letterGlyphs] = useState(() => JSON.parse(sessionStorage.getItem('output:glyphs:letters') || '[]'));

  // Load emoji cache
  useEffect(() => ensureEmojiCache(), []);

  // Cleanup uploads on unmount
  useEffect(() => {
    const sessionKey = uploadSessionKey;
    const workspaceId = uploadWorkspaceId;
    return () => {
      removeMediaAttachmentsForSessionKey(sessionKey, { workspaceId }).catch((error) => {
        logError('FlexiconEditor', 'Failed to cleanup uploads', { error });
      });
    };
  }, [uploadSessionKey, uploadWorkspaceId]);

  // Emojibar click-away handling
  const emojibarContentRef = useClickAway(() => setIsEmojibarOpen(false));

  // Register emojibar with KeyMaster when open
  const emojibarKeychainId = isEmojibarOpen ? KEYCHAIN.POPOVER__FLEXICON_EMOJIBAR : null;
  const { unlocked: emojibarUnlocked } = useRegisterWithKeyMaster(emojibarKeychainId);

  // Close emojibar on Escape key
  useHotkeys(
    'escape',
    () => {
      if (emojibarUnlocked()) setIsEmojibarOpen(false);
    },
    { enabled: isEmojibarOpen, enableOnFormTags: true, preventDefault: true },
    [emojibarUnlocked],
  );

  // Save flexicon based on the resource type
  const saveFlexicon = useCallback(
    async (flexiconType, flexiconId, { throwOnError = false } = {}) => {
      if (!resourceId || readOnly) return false;

      setIsSaving(true);
      if (flexiconType === 'image' && flexiconId) setSavedAttachmentId(flexiconId);

      try {
        const path = resolveFlexiconSavePath(resourceType, resourceId);
        if (!path) {
          const savePathError = new Error('Unknown resourceType for save path');
          if (throwOnError) throw savePathError;
          logError('FlexiconEditor', savePathError.message, { resourceType, resourceId });
          setUploadError(SAVE_ERROR_MESSAGES_BY_RESOURCE_TYPE[resourceType]);
          return false;
        }

        const patchFlexicon = resourceType === 'user' ? apiPatchAsUser : apiPatch;
        const response = await patchFlexicon(path, {
          [resourceType]: { flexicon_type: flexiconType, flexicon_id: flexiconId },
        });
        const updated = await persistFlexiconSaveResponse({ database: db, resourceType, response });

        if (updated.flexicon != null) setPreviewFlexicon(updated.flexicon);
        onSaveComplete?.();
        return true;
      } catch (error) {
        logError('FlexiconEditor', 'Failed to save flexicon', { error });
        if (throwOnError) throw error;
        setUploadError(SAVE_ERROR_MESSAGES_BY_RESOURCE_TYPE[resourceType]);
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [resourceId, resourceType, readOnly, onSaveComplete],
  );

  // Notify parent (unsaved) or PATCH the server (saved) with the flexicon change.
  // onFlexiconChange is always called immediately so callers don't have to wait for
  // the workspace prop to propagate back before knowing the new effective kind.
  const applyFlexiconChange = useCallback(
    (flexiconType, flexiconId) => {
      if (readOnly) return Promise.resolve(false);
      onFlexiconChange?.({ flexicon_type: flexiconType, flexicon_id: flexiconId });
      if (isUnsaved) return Promise.resolve(true);
      return saveFlexicon(flexiconType, flexiconId);
    },
    [readOnly, isUnsaved, onFlexiconChange, saveFlexicon],
  );

  // Auto-save when upload completes (or notify parent if unsaved)
  useEffect(() => {
    const attachmentId = currentUpload?.attachment_id;
    if (readOnly || !isUploadComplete || !attachmentId || attachmentId === savedAttachmentId || isSaving) return;

    logDebug('FlexiconEditor', 'Upload complete', { attachmentId, isUnsaved });
    const uploadedImage = {
      id: attachmentId,
      file_name: currentUpload?.metadata?.file_name,
      public_url: currentUpload?.public_url,
      variants: currentUpload?.variants || {},
    };

    applyFlexiconChange('image', attachmentId).then((saved) => {
      if (!saved) return;

      // Keep the uploaded image visible after the upload row is cleaned up.
      if (resolveImageFlexiconUrl(uploadedImage)) {
        setPreviewFlexicon({
          kind: 'image',
          image: uploadedImage,
        });
      }
      setCurrentUploadId(null);
      removeMediaAttachmentsForSessionKey(uploadSessionKey, { workspaceId: uploadWorkspaceId }).catch((error) => {
        logError('FlexiconEditor', 'Failed to cleanup after upload', { error });
      });
    });
  }, [
    isUploadComplete,
    currentUpload?.attachment_id,
    currentUpload?.metadata?.file_name,
    currentUpload?.public_url,
    currentUpload?.variants,
    savedAttachmentId,
    isSaving,
    applyFlexiconChange,
    uploadSessionKey,
    uploadWorkspaceId,
    isUnsaved,
    readOnly,
  ]);

  // drop optimistic/server preview once dexie/live resource matches (same visual as Flexicon memo)
  useEffect(() => {
    if (!previewFlexicon) return;
    if (getFlexiconSignature({ flexicon: previewFlexicon }) === getFlexiconSignature(resource)) setPreviewFlexicon(null);
  }, [resource, previewFlexicon]);

  const handleUploadClick = useCallback(() => {
    if (readOnly) return;
    fileInputRef.current?.click();
  }, [readOnly]);

  const openUploadPicker = useCallback(() => {
    if (isDisabled) return;
    handleUploadClick();
  }, [handleUploadClick, isDisabled]);

  useImperativeHandle(ref, () => ({ openUploadPicker }), [openUploadPicker]);

  // Shared file handling logic for both file input and dropzone
  const handleFile = useCallback(
    async (file) => {
      if (readOnly || !file) return;

      // Validate file type
      if (!FLEXICON_IMAGE_CONTENT_TYPES.includes(file.type)) {
        logError('FlexiconEditor', 'Invalid file type', { type: file.type });
        return;
      }

      // Clear previous upload
      setUploadError(null);
      setCurrentUploadId(null);
      setSavedAttachmentId(null);
      setPreviewFlexicon(null);

      if (resourceType === 'user') {
        setUserUploadProgress(0);
        setIsUserUploadProcessing(false);
        setUserUploadPreviewUrl(URL.createObjectURL(file));

        try {
          const attachment = await uploadUserFlexicon(file, {
            onProgress: setUserUploadProgress,
            onProcessing: () => setIsUserUploadProcessing(true),
          });
          await saveFlexicon('image', attachment.id, { throwOnError: true });
        } catch (error) {
          logError('FlexiconEditor', 'Failed to upload or save user flexicon', { error });
          setPreviewFlexicon(null);
          setUploadError('Unable to save avatar. Please try again.');
        } finally {
          setUserUploadPreviewUrl(null);
          setUserUploadProgress(0);
          setIsUserUploadProcessing(false);
        }
        return;
      }

      await removeMediaAttachmentsForSessionKey(uploadSessionKey, { workspaceId: uploadWorkspaceId });

      try {
        const purpose = UPLOAD_PURPOSE_BY_RESOURCE_TYPE[resourceType];
        if (!purpose) {
          logError('FlexiconEditor', 'Unknown resourceType for upload purpose', { resourceType });
          return;
        }
        const uploadId = await enqueueUpload({
          file,
          purpose,
          sessionKey: uploadSessionKey,
          channelId: resourceType === 'channel' ? resourceId : null,
          workspaceId: uploadWorkspaceId,
        });
        setCurrentUploadId(uploadId);
      } catch (error) {
        logError('FlexiconEditor', 'Failed to start upload', { error });
        setPreviewFlexicon(null);
      }
    },
    [readOnly, uploadSessionKey, uploadWorkspaceId, resourceType, resourceId, saveFlexicon],
  );

  const handleFileSelect = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      handleFile(file);

      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [handleFile],
  );

  // Dropzone for drag-and-drop uploads
  const handleDrop = useCallback(
    (acceptedFiles) => {
      handleFile(acceptedFiles[0]);
    },
    [handleFile],
  );

  const dropzoneAccept = useMemo(
    () =>
      FLEXICON_IMAGE_CONTENT_TYPES.reduce((acc, type) => {
        acc[type] = [];
        return acc;
      }, {}),
    [],
  );

  const { getRootProps, isDragActive } = useDropzone({
    noClick: true,
    multiple: false,
    disabled: isDisabled,
    accept: dropzoneAccept,
    onDrop: handleDrop,
  });

  const handlePastedFiles = useCallback(
    (files) => {
      const file = files.find((candidate) => FLEXICON_IMAGE_CONTENT_TYPES.includes(candidate.type));
      handleFile(file);
    },
    [handleFile],
  );

  useScopedClipboardFiles({ rootRef, onFiles: handlePastedFiles, isEnabled: !isDisabled, keychainId });

  const handleEmojiClick = useCallback(() => {
    if (readOnly) return;
    setIsEmojibarOpen(true);
  }, [readOnly]);

  const handleEmojiSelect = useCallback(
    (emoji, context = {}) => {
      if (readOnly) return;
      setUploadError(null);
      logDebug('FlexiconEditor', 'Emoji selected', { emoji });
      setPreviewFlexicon({
        kind: 'emoji',
        emoji: { id: emoji.id, public_url: emoji.public_url, native: emoji.native, shortcode: emoji.shortcode },
      });
      applyFlexiconChange('emoji', emoji.id);
      if (!context.keepOpen) setIsEmojibarOpen(false);
    },
    [readOnly, applyFlexiconChange],
  );

  const handleRemoveClick = useCallback(async () => {
    if (readOnly) return;
    setUploadError(null);
    if (resourceType === 'workspace' && letterGlyphs.length) {
      // revert to the letter glyph for the current title — show immediately, then save
      const glyph = findLetterGlyph(letterGlyphs, nameValue);
      if (glyph) {
        setPreviewFlexicon({ kind: 'glyph', glyph });
        await applyFlexiconChange('glyph', glyph.id);
        return;
      }
    }
    if (isUnsaved) setPreviewFlexicon(null);
    await applyFlexiconChange(null, null);
    // saved resources: preview is set from PATCH json in saveFlexicon so we are not stuck on stale dexie
  }, [readOnly, applyFlexiconChange, resourceType, letterGlyphs, nameValue, isUnsaved]);

  // Build the resource object for Flexicon preview.
  // For workspace with a letter glyph, derive the preview from the nameValue label
  // so the icon updates live as the user types — without auto-saving.
  const previewResource = useMemo(() => {
    if (previewFlexicon) return { flexicon: previewFlexicon };

    if (nameValue && resourceType === 'workspace' && letterGlyphs.length) {
      const savedKind = resource?.flexicon?.kind;
      // only drive from name when the current flexicon is a letter glyph (not a user-set emoji/image/other glyph)
      const isLetterGlyph = savedKind === 'glyph' && resource?.flexicon?.glyph?.kind === 'letters';
      if (isLetterGlyph || !savedKind) {
        const glyph = findLetterGlyph(letterGlyphs, nameValue);
        if (glyph) return { flexicon: { kind: 'glyph', glyph } };
      }
    }

    return resource;
  }, [previewFlexicon, nameValue, resourceType, resource, letterGlyphs]);

  // glyphs are the default/assigned type — only image/emoji count as an explicitly set flexicon
  const activeKind = previewFlexicon?.kind ?? resource?.flexicon?.kind;
  const hasFlexicon = activeKind === 'image' || activeKind === 'emoji';

  const previewClassName = cn(
    'relative flex-shrink-0 overflow-hidden',
    previewSize,
    previewRounded,
    readOnly ? '' : 'cursor-pointer disabled:cursor-not-allowed',
  );

  const previewContents = (
    <>
      {userUploadPreviewUrl ? (
        <>
          <img src={userUploadPreviewUrl} alt="" className="absolute inset-0 size-full object-cover" draggable="false" />
          <span className="absolute inset-0 flex items-center justify-center bg-black/40 text-sm font-medium text-white">
            {isUserUploadProcessing ? '...' : `${userUploadProgress}%`}
          </span>
        </>
      ) : showUploadPreview ? (
        <MediaAttachmentUploadPreview
          mediaAttachment={currentUpload}
          size={previewSize}
          className={previewRounded}
          showRemoveButton={false}
          showTint={false}
          objectFit="cover"
        />
      ) : (
        <Flexicon resource={previewResource} size={previewSize} rounded={previewRounded} />
      )}
      {!readOnly && isDragActive && <div className={cn('absolute inset-0 bg-black/50', previewRounded)} />}
    </>
  );

  const isStackedLayout = layout === 'stacked';
  const buttonLabels = BUTTON_LABELS_BY_RESOURCE_TYPE[resourceType];
  const uploadActionLabel =
    isUploading || isProcessing || isSaving || isUserUploading || isUserUploadProcessing ? uploadBusyLabel : uploadLabel || buttonLabels.upload;

  return (
    <div
      ref={rootRef}
      className={isStackedLayout ? 'flex flex-col items-center gap-4' : 'flex flex-col items-center gap-5 md:flex-row'}
    >
      <div className="group relative">
        {readOnly ? (
          <div className={previewClassName}>{previewContents}</div>
        ) : (
          <button {...getRootProps()} type="button" onClick={handleUploadClick} disabled={isDisabled} className={previewClassName}>
            {previewContents}
          </button>
        )}
        {/* Outside the clipped preview so the badge overlaps the circle's edge; pointer-events
            pass through to the upload button underneath, so hover rides the wrapper group. */}
        {!readOnly && actionsPresentation === 'overlay' && (
          <span className="pointer-events-none absolute right-0 bottom-0 flex size-9 items-center justify-center rounded-full bg-black/40 text-white shadow-[0px_4px_4px_rgba(0,0,0,0.02)] backdrop-blur-[10px] group-hover:bg-black/60">
            <Upload size={18} strokeWidth={1.5} absoluteStrokeWidth />
          </span>
        )}
      </div>

      {!readOnly && uploadOnly && showUploadButton ? (
        <button
          type="button"
          onClick={handleUploadClick}
          disabled={isDisabled}
          className={cn(
            'cursor-pointer text-center [font-family:Inter,var(--font-sans)] text-xs leading-[22px] font-normal text-gray-600 hover:text-gray-800 disabled:cursor-wait disabled:opacity-70',
            uploadButtonClassName,
          )}
        >
          {uploadActionLabel}
        </button>
      ) : null}

      {uploadError ? <p className="max-w-56 text-center text-xs text-red-500 dark:text-red-400">{uploadError}</p> : null}

      {!readOnly && !uploadOnly && actionsPresentation !== 'overlay' && (
        <div className="flex items-center justify-center gap-2">
          {hasFlexicon ? (
            <ActionButton onClick={handleRemoveClick} disabled={isDisabled}>
              {buttonLabels.remove}
            </ActionButton>
          ) : (
            <>
              <ActionButton onClick={handleUploadClick} disabled={isDisabled}>
                {buttonLabels.upload}
              </ActionButton>
              <ActionButton onClick={handleEmojiClick} disabled={isDisabled}>
                Emoji
              </ActionButton>
            </>
          )}
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={FLEXICON_IMAGE_CONTENT_TYPES.join(',')}
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Emojibar */}
      {isEmojibarOpen && (
        <FloatingPortal>
          <div className="fixed inset-0 z-(--stack-emoji-picker) flex items-center justify-center bg-black/50">
            <div ref={emojibarContentRef}>
              <EmojibarPopover onEmojiSelect={handleEmojiSelect} allowMultiSelect={false} allowCustom={resourceType !== 'user'} />
            </div>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
});

export default FlexiconEditor;
